import React, { useState, useEffect} from "react";
import {
  Box,
  Button,
  VStack,
  Text,
  HStack,
  Center,
  Spinner,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  InputGroup,
  InputLeftElement,
  Input,
  AlertDialog,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogFooter,
  Icon,
} from "@chakra-ui/react";
import { AddIcon, ArrowBackIcon, ChevronDownIcon, DeleteIcon, SearchIcon } from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";
import * as MetricService from "../../services/MetricService";
import showToast from "hooks/useCustomToast";
import { useConfigurationContext } from "context/ConfigurationContext";
import { BenchmarkType } from "build-ts/enums";
import { DatePeriodEnum } from "build-ts/relative_date";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import CreateBenchmarkComponent from "components/configuration/mkg/CreateBenchmarkComponent";
import EditBenchmarkComponent from "./mkg/EditBenchmarkComponent";
import CustomModal from "../modals/CustomModal";
import DependenciesModal from "./DependenciesModal";
import { MdEdit } from "react-icons/md";
import { FaCheckCircle, FaFilter } from "react-icons/fa";
import { PiTarget } from "react-icons/pi";

interface BenchmarkListComponentProps {
  onBack: () => void;
  benchmarkList: Header[];
  setBenchmarkList: React.Dispatch<React.SetStateAction<Header[]>>;
  onReloadKnowledgeGraph?: () => void;
}

const BenchmarkListComponent: React.FC<BenchmarkListComponentProps> = ({ 
  onBack, 
  benchmarkList,
  setBenchmarkList,
  onReloadKnowledgeGraph,
 }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const {
    onOpen,
    isOpen,
    onClose,
    editBenchmark, 
    selectedBenchmark, 
    setSelectedBenchmark,
    setSelectedMetric,
    setSelectedKgColumn,
    selectedBenchmarkId, 
    deleteBenchmarkDialog,
    handleEditBenchmark,
    handleDeleteBenchmarkDialog,
    handleDeleteBenchmark,
    closeDeleteBenchmarkDialog,
    closeBenchmarkModal,
    dependencies,
    showDependenciesModal,
    closeDeleteBenchmarkModal,
  } = useConfigurationContext();
  const cancelDeleteRef = React.useRef<HTMLButtonElement>(null);
  const [triggerSubmit, setTriggerSubmit] = useState<boolean>(false);

  useEffect(() => {
    if(benchmarkList.length === 0) {
      fetchAllBenchmarks();
    }
    else {
      setIsLoading(false);
    }
  }, []);

  const fetchAllBenchmarks = async () => {
    setIsLoading(true);
    try {
      const data = await MetricService.getAllBenchmarks();
      if(data) {
        setBenchmarkList(data);
      }
    } catch (error) {
      showToast("Error fetching benchmarks", "", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to trigger submit
  const handleSave = () => {
    setTriggerSubmit(true);
  };

  // Save button
  const saveButton = (
    <Button
      colorScheme="green"
      size="sm"
      onClick={handleSave}
      leftIcon={<FaCheckCircle />}
      fontWeight="bold"
    >
      Save
    </Button>
  );

  const handleBenchmarkClick = (benchmark: Header) => {
    if(selectedBenchmark?.id === benchmark.id) {
      setSelectedBenchmark(null);  
    } else {
      setSelectedBenchmark(benchmark);
    }
    setSelectedMetric(null);
    setSelectedKgColumn(null);
  };

  const filteredBenchmarks = benchmarkList.filter(benchmark =>{
    return benchmark?.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleCreateBenchmark = async (
    name: string,
    type: BenchmarkType,
    granularity?: DatePeriodEnum
  ) => {
    try {
      // Create new benchmark entity
      const newBenchmark = BenchmarkEntityProto.create({
        header: {
          name,
          id: "", // Will be assigned by the server
        },
        benchmark_type: type,
        versus_filters: [],
        definition_mapping: [],
        // Use provided granularity or default to DAY for TARGET_BENCHMARK
        granularity: type === BenchmarkType.TARGET_BENCHMARK ? (granularity || DatePeriodEnum.DAY) : undefined,
      });

      const savedBenchmark = await MetricService.saveBenchmark(newBenchmark);
      if (savedBenchmark) {
        showToast("Benchmark created successfully", "", "success");
        fetchAllBenchmarks(); // Refresh the benchmarks list
        handleEditBenchmark(savedBenchmark?.header!);
      }
    } catch (error) {
      showToast("Error creating benchmark", "", "error");
    }
  };

  const handleOpenBenchmarkModal = () => {
    onOpen();
  };

  const handleCloseEditBenchmarkModal = () => {
    closeBenchmarkModal();
  };

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      {/* Back Button, Search, and Header in same line */}
      <Box px={3} py={3} flexShrink={0}>
        <HStack spacing={3} alignItems="center" width="100%">
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon boxSize={5} />}
            variant="tertiary"
            size="xs"
            onClick={onBack}
            _hover={{ bg: "purple.50", color: "black" }}
            flexShrink={0}
            fontSize="sm"
          />
          
          <InputGroup flex="1" maxW="400px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search benchmarks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
              size="sm"
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
            />
          </InputGroup>
          
          <Button
            onClick={() => handleOpenBenchmarkModal()}
            leftIcon={<AddIcon boxSize={3}/>}
            size="xs"
            aria-label="Create New Plan"
            borderRadius="md"
            title="Create New Plan"
            bg="gray.50"
            color="purple.700"
            _hover={{ bg: "purple.700", color: "white" }}
            variant="outline"
            borderWidth="2px"
            borderColor="purple.700"
            fontWeight="700"
            iconSpacing={1}
            cursor="pointer"
          >
            New
          </Button>
        </HStack>
      </Box>
      <Box 
        flex="1" 
        overflowY="auto"
        overflowX="hidden"
        px={4}
        pb={14}
        minHeight={0}
        sx={{
          "::-webkit-scrollbar": {
            width: "6px",
          },
          "::-webkit-scrollbar-track": {
            background: "gray.100",
            borderRadius: "3px",
          },
          "::-webkit-scrollbar-thumb": {
            background: "purple.300",
            borderRadius: "3px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "purple.400",
          },
        }}
      >
        {isLoading ? (
          <Center height="100%">
            <Spinner size="md" color="purple.500" />
          </Center>
        ) : (
          <VStack spacing={2} align="stretch">
            {/* AGENTS Heading with Filter */}
          <HStack justify="space-between" align="center" mx={1} p={0}>
            <Icon as={PiTarget} boxSize={5} color="gray.500"/>
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              Benchmarks
            </Text>
            {/* Divider Line */}
            <Box 
              flex="1" 
              height="1px" 
              bg="gray.300" 
              mx={0}
            />
            <IconButton
              aria-label="Filter benchmarks"
              icon={<Icon as={FaFilter} />}
              size="xs"
              variant="ghost"
              color="gray.500"
              _hover={{ color: "purple.600", bg: "gray.50" }}
              cursor="pointer"
            />
          </HStack>
            { filteredBenchmarks.length > 0 ? (
              filteredBenchmarks.map((benchmark) => (
                <Button
                  key={benchmark.id}
                  variant="outline"
                  size="sm"
                  justifyContent="flex-start"
                  borderColor="gray.300"
                  _hover={{ 
                    bg: selectedBenchmark?.id === benchmark.id ? "purple.700" : "purple.50", 
                    color: selectedBenchmark?.id === benchmark.id ? "white" : "gray.800"
                  }}
                  bg={selectedBenchmark?.id === benchmark.id ? "purple.700" : "white"}
                  color={selectedBenchmark?.id === benchmark.id ? "white" : "gray.800"}
                  borderRadius="md"
                  fontSize="sm"
                  height="auto"
                  py={1}
                  whiteSpace="normal"
                  textAlign="left"
                  onClick={() => handleBenchmarkClick(benchmark)}
                >
                  {/* {benchmark.name} */}
                  <HStack justifyContent="space-between" width="100%">
                    <Text mb={0}>{benchmark.name}</Text>
                    <Menu>
                      <MenuButton 
                        as={IconButton}
                        aria-label="Options"
                        borderRadius="full"
                        icon={<ChevronDownIcon />}
                        variant="ghost"
                        size="sm"
                        onClick={(event) => event.stopPropagation()}
                      />
                      <MenuList>
                      <MenuItem
                        fontSize="sm"
                        fontWeight={600}
                        icon={<MdEdit />}
                        onClick={() => {
                          setSelectedBenchmark(null as any);
                          handleEditBenchmark(benchmark);
                        }}
                        cursor="pointer"
                        color="gray.800"
                        _hover={{ bg: "gray.50" }}
                        py={2}
                        px={3}
                      >
                        Definition
                      </MenuItem>
                      <MenuItem
                        fontSize="sm"
                        fontWeight={600}
                        icon={<DeleteIcon />}
                        onClick={() => {
                          setSelectedBenchmark(null as any);
                          handleDeleteBenchmarkDialog(benchmark.id!);
                        }}
                        cursor="pointer"
                        color="red.500"
                        _hover={{ bg: "gray.50" }}
                        py={2}
                        px={3}
                      >
                        Delete
                      </MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Button>
              ))
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                No benchmarks found
              </Text>
            )}
          </VStack>
        )}
      </Box>
      {isOpen && (
        <CustomModal
          isOpen={isOpen}
          onClose={onClose}
          title="Create New Benchmark"
          size="md"
        >
          <CreateBenchmarkComponent
            onClose={onClose}
            onCreateBenchmark={handleCreateBenchmark}
          />
        </CustomModal>
      )}
    {editBenchmark && (
      <CustomModal
        isOpen={editBenchmark}
        onClose={handleCloseEditBenchmarkModal}
        title={`Edit Benchmark - ${selectedBenchmarkId?.header?.name}`}
        size="6xl"
        headerAction={saveButton}
      >
        <EditBenchmarkComponent
          benchmark={selectedBenchmarkId!}
          onClose={handleCloseEditBenchmarkModal}
          onSave={fetchAllBenchmarks}

          // For saving the Benchmark
          triggerSubmit={triggerSubmit}
          setTriggerSubmit={setTriggerSubmit}
          onReloadKnowledgeGraph={onReloadKnowledgeGraph}
        />
      </CustomModal>
    )}
    {deleteBenchmarkDialog &&  (
      <AlertDialog 
        isOpen={deleteBenchmarkDialog}
        leastDestructiveRef={cancelDeleteRef}
        onClose={closeDeleteBenchmarkDialog}
      >
        <AlertDialogOverlay
          style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
        >
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Metric
            </AlertDialogHeader>

            <AlertDialogBody>
                Are you sure you want to delete this benchmark?
            </AlertDialogBody>

            <AlertDialogFooter>
                <Button
                ref={cancelDeleteRef}
                colorScheme="green"
                onClick={closeDeleteBenchmarkDialog}
                >
                Cancel
                </Button>
                <Button
                colorScheme="red"
                onClick={() => handleDeleteBenchmark(selectedBenchmark?.id!)}
                ml={3}
                >
                Delete
                </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    )}
    <DependenciesModal
      isOpen={showDependenciesModal}
      onClose={closeDeleteBenchmarkModal}
      dependencies={dependencies}
    />
    </Box>
  );
};

export default BenchmarkListComponent;